export const translations = {
  en: {
    // App Titles
    title: "AI-Powered Customer Analytics",
    // Slide Titles
    introduction: "Introduction",
    businessChallenges: "Business Challenges",
    solution: "AI Solution",
    technologyOverview: "Platform Technology",
    industryApplications: "Industry Applications",
    successStory: "Success Story",
    implementation: "Implementation Plan",
    gettingStarted: "Get Started",
    // Navigation
    previous: "Previous",
    next: "Next",
    slide: "Slide",
    of: "of",
    // IntroductionSlide (Slide 1 & 2)
    slide1Title: "AI-Powered Customer Analytics",
    slide1Subtitle: "Understanding Customer Analytics in the AI Era",
    agendaTitle: "Today's Discussion:",
    agenda1: "What is Customer Analytics and why does it matter?",
    agenda2: "The evolution from traditional to AI-powered analytics",
    agenda3: "Key business challenges Customer Analytics solves",
    agenda4: "Our comprehensive Customer Analytics solution",
    agenda5: "Real-world applications and success stories",
    agenda6: "How to get started with Customer Analytics",
    slide2Title: "What is Customer Analytics?",
    slide2Definition: "The systematic examination of customer data to understand behavior patterns, preferences, and trends that drive business decisions and improve customer experiences.",
    slide2BottomLine: "Customer Analytics transforms raw data into strategic business intelligence.",
    coreComponentsTitle: "Core Components",
    dataCollection: "Data Collection",
    patternRecognition: "Pattern Recognition",
    segmentation: "Segmentation",
    predictiveModeling: "Predictive Modeling",
    actionableInsights: "Actionable Insights",
    keyQuestionsTitle: "Key Questions Answered",
    qValuableCustomers: "Who are my most valuable customers?",
    qPurchaseDecisions: "What drives customer purchase decisions?",
    qBuyOrChurn: "When are customers most likely to buy or churn?",
    qRecommendations: "Which products should I recommend to specific customers?",
    qOptimizeJourney: "How can I optimize the customer journey?",
    // BusinessChallengesSlide (Slide 3)
    slide3Title: "Why Customer Analytics is Critical Today",
    slide3Subtitle: "The Business Imperative",
    marketRealitiesTitle: "Market Realities Driving Demand",
    realityTitle1: "Customer Expectations",
    reality1Point1: "80% expect personalized experiences",
    reality1Point2: "73% willing to pay more for personalization",
    reality1Point3: "91% prefer brands with relevant recommendations",
    realityTitle2: "Economic Pressures",
    reality2Point1: "Acquisition costs up 60% YoY",
    reality2Point2: "Retention is 5-25x cheaper than acquisition",
    reality2Point3: "Analytics users 23% more likely to outperform",
    realityTitle3: "Data Explosion",
    reality3Point1: "2.5 quintillion bytes of data daily per customer",
    reality3Point2: "90% of world's data from last 2 years",
    reality3Point3: "Traditional methods can't handle the volume",
    consequencesTitle: "Business Consequences Without Analytics",
    consequenceTitle1: "Lost Revenue",
    consequenceText1: "70% cart abandonment rates in ecommerce",
    consequenceTitle2: "Wasted Marketing",
    consequenceText2: "50% of budget spent on wrong customers",
    consequenceTitle3: "Missed Opportunities",
    consequenceText3: "85% can't predict customer churn",
    consequenceTitle4: "Competitive Disadvantage",
    consequenceText4: "Competitors with AI gain 15-20% market share",
    slide3BottomLine: "In today's market, customer analytics isn't optional—it's essential for survival.",
    // SolutionSlide (Slide 4 & 5)
    slide4Title: "Traditional vs. AI-Powered Analytics",
    slide4Subtitle: "The Evolution of Customer Understanding",
    capability: "Capability",
    traditional: "Traditional",
    aiPowered: "AI-Powered",
    dataProcessing: "Data Processing",
    batchProcessing: "Batch processing, weekly/monthly reports",
    realTimeProcessing: "Real-time processing, instant insights",
    analysisScope: "Analysis Scope",
    historical: "Historical data, what happened",
    predictive: "Predictive & prescriptive analytics",
    personalization: "Personalization",
    basicSegmentation: "Basic segmentation (age, location)",
    individualized: "Individual-level customization",
    patternDetection: "Pattern Detection",
    manual: "Human-driven, limited variables",
    automated: "AI identifies hidden patterns",
    actionability: "Actionability",
    manualInterpretation: "Requires manual interpretation",
    automatedRecommendations: "Automated recommendations & alerts",
    slide5Title: "Key Business Problems Solved",
    slide5Subtitle: "Addressing Your Biggest Challenges",
    challenge: "Challenge",
    aiSolution: "AI Solution",
    result: "Result",
    problem1Title: "Customer Churn",
    problem1Challenge: "Average 25% annual churn rate; replacement costs 5-25x more.",
    problem1Solution: "Predictive churn modeling identifies at-risk customers 30-90 days in advance.",
    problem1Result: "Up to 65% reduction in churn rate.",
    problem2Title: "Ineffective Personalization",
    problem2Challenge: "Generic experiences have <2% conversion rates.",
    problem2Solution: "Real-time personalization engine provides individual-level recommendations.",
    problem2Result: "Up to 34% increase in conversion rates.",
    problem3Title: "Poor Marketing ROI",
    problem3Challenge: "50% of marketing spend goes to low-value prospects.",
    problem3Solution: "Intelligent targeting with predictive lifetime value modeling.",
    problem3Result: "Up to 240% improvement in marketing ROI.",
    problem4Title: "Cart Abandonment",
    problem4Challenge: "Average 70% cart abandonment, $4.6T lost globally.",
    problem4Solution: "Smart recovery with real-time intervention triggers.",
    problem4Result: "Up to 92% increase in cart recovery rate.",
    // TechnologyOverviewSlide (Slide 6)
    slide6Title: "Our AI-Powered Customer Analytics Solution",
    slide6Subtitle: "Comprehensive Intelligence Platform",
    architectureTitle: "Platform Architecture",
    layer1Title: "Data Integration",
    layer1Point1: "Connect to 50+ data sources",
    layer1Point2: "Real-time processing (10M+ events/hr)",
    layer1Point3: "Automated data quality",
    layer2Title: "AI Analytics Engine",
    layer2Point1: "Advanced ML algorithms",
    layer2Point2: "Industry-specific models",
    layer2Point3: "Continuous learning",
    layer3Title: "Intelligence Dashboard",
    layer3Point1: "Real-time insights & alerts",
    layer3Point2: "Predictive recommendations",
    layer3Point3: "Custom reporting",
    layer4Title: "Action Layer",
    layer4Point1: "Automated campaign triggers",
    layer4Point2: "API integrations",
    layer4Point3: "Performance tracking",
    capabilitiesTitle: "Core Capabilities",
    capability1Title: "Real-Time Intelligence",
    capability1Point1: "Live activity monitoring",
    capability1Point2: "Instant churn alerts",
    capability1Point3: "Dynamic segmentation",
    capability2Title: "Predictive Analytics",
    capability2Point1: "Lifetime value forecasting (87% accuracy)",
    capability2Point2: "Churn prediction (94% accuracy)",
    capability2Point3: "Purchase intent modeling",
    capability2Point4: "Seasonal demand forecasting",
    capability3Title: "AI-Powered Recommendations",
    capability3Point1: "Personalized product suggestions",
    capability3Point2: "Cross-sell & upsell opportunities",
    capability3Point3: "Optimal timing & channel",
    capability3Point4: "Content personalization",
    capability4Title: "Business Intelligence",
    capability4Point1: "Customer journey mapping",
    capability4Point2: "Revenue attribution analysis",
    capability4Point3: "Campaign performance measurement",
    capability4Point4: "Competitive benchmarking",
    // IndustryApplicationsSlide (Slide 7)
    slide7Title: "Industry-Specific Applications",
    slide7Subtitle: "Tailored Solutions for Your Business",
    useCases: "Key Use Cases:",
    typicalResults: "Typical Results:",
    industry1Title: "Ecommerce & Retail",
    industry1Case1: "Product recommendation engines",
    industry1Case2: "Dynamic pricing optimization",
    industry1Case3: "Inventory demand forecasting",
    industry1Case4: "Customer journey optimization",
    industry1Result1: "23% increase in AOV",
    industry1Result2: "45% improvement in conversion rates",
    industry1Result3: "32% reduction in inventory costs",
    industry2Title: "B2B & SaaS",
    industry2Case1: "Account expansion opportunities",
    industry2Case2: "Customer health scoring",
    industry2Case3: "Renewal probability prediction",
    industry2Case4: "Sales pipeline optimization",
    industry2Result1: "28% increase in expansion revenue",
    industry2Result2: "67% improvement in renewal rates",
    industry2Result3: "34% reduction in CAC",
    industry3Title: "Healthcare & Professional Services",
    industry3Case1: "Patient/client journey optimization",
    industry3Case2: "Service utilization prediction",
    industry3Case3: "Satisfaction and retention modeling",
    industry3Case4: "Resource allocation optimization",
    industry3Result1: "19% improvement in satisfaction",
    industry3Result2: "25% increase in service utilization",
    industry3Result3: "31% reduction in no-show rates",
    industry4Title: "Financial Services",
    industry4Case1: "Customer lifetime value modeling",
    industry4Case2: "Cross-sell optimization",
    industry4Case3: "Risk assessment & fraud detection",
    industry4Case4: "Digital engagement optimization",
    industry4Result1: "42% increase in cross-sell success",
    industry4Result2: "58% improvement in digital adoption",
    industry4Result3: "29% reduction in customer churn",
    // SuccessStorySlide (Slide 8)
    slide8Title: "Success Story: Home Decor Ecommerce",
    slide8Subtitle: "Real Results from a Real Implementation",
    clientBackgroundTitle: "Client Background",
    clientBackgroundContent: "Mid-size online retailer ($50M revenue, 2M+ customers) struggling with cart abandonment and declining customer LTV.",
    challengesTitle: "Challenges",
    challenge1: "70% cart abandonment rate",
    challenge2: "Decreasing repeat purchase rates",
    challenge3: "Ineffective email marketing",
    challenge4: "Unable to predict seasonal demand",
    solutionTitle: "Solution Implemented",
    phase1: "Weeks 1-4: Integrated data sources and implemented real-time behavioral tracking.",
    phase2: "Weeks 5-8: Deployed predictive churn/LTV models and a personalized recommendation engine.",
    phase3: "Weeks 9-12: Fine-tuned AI models and expanded personalization to email campaigns.",
    resultsTitle: "Quantified Results (90 Days)",
    metric: "Metric",
    before: "Before",
    after: "After",
    improvement: "Improvement",
    metric1: "Cart Recovery Rate",
    metric2: "Customer Lifetime Value",
    metric3: "Email Click-through Rate",
    metric4: "Conversion Rate",
    metric5: "Average Order Value",
    revenueImpactTitle: "Revenue Impact",
    impact1Value: "$248,000",
    impact1Desc: "additional monthly revenue",
    impact2Value: "$2.97M",
    impact2Desc: "projected annual impact",
    impact3Value: "534%",
    impact3Desc: "ROI in first year",
    keyInsightsTitle: "Key AI Insights Delivered",
    insight1: "Customers viewing lighting + furniture have 45% higher LTV.",
    insight2: "Sunday 7 PM is the optimal time for email campaigns (+34% engagement).",
    insight3: "Minimalist style trend detected 2 months before competitors.",
    // ImplementationSlide (Slide 9)
    slide9Title: "Implementation Approach & Timeline",
    slide9Subtitle: "Your Path to Customer Analytics Success",
    frameworkTitle: "Our Proven 90-Day Framework",
    phase1Title: "Discovery",
    phase1Duration: "Weeks 1-3",
    phase1Item1: "Data Assessment",
    phase1Item2: "Business Alignment",
    phase1Item3: "Infrastructure Setup",
    phase1Item4: "Baseline Measurement",
    phase2Title: "AI Deployment",
    phase2Duration: "Weeks 4-8",
    phase2Item1: "Model Training",
    phase2Item2: "Dashboard Implementation",
    phase2Item3: "Team Training",
    phase2Item4: "Initial Optimization",
    phase3Title: "Optimization",
    phase3Duration: "Weeks 9-12",
    phase3Item1: "Performance Tuning",
    phase3Item2: "Activate Advanced Features",
    phase3Item3: "Success Measurement",
    phase3Item4: "Roadmap Planning",
    riskMitigationTitle: "Risk Mitigation",
    risk1: "Phased Approach: Value delivery at each stage.",
    risk2: "Success Metrics: Clear KPIs and measurement framework.",
    risk3: "Continuous Support: Dedicated customer success team.",
    risk4: "Performance Guarantees: ROI commitments.",
    teamRoleTitle: "Your Team's Role",
    role1: "Minimal IT Resources (2-4 hours/week).",
    role2: "Business Stakeholder Input (weekly check-ins).",
    role3: "Change Management support provided.",
    role4: "Ongoing partnership for optimization.",
    // GettingStartedSlide (Slide 10)
    slide10Title: "Getting Started",
    slide10Subtitle: "Ready to Transform Your Customer Understanding?",
    nextStepsTitle: "Next Steps",
    investmentTitle: "Investment",
    step1Title: "Free Customer Analytics Assessment",
    step1Desc: "Audit your current capabilities and get a custom ROI projection.",
    step2Title: "Pilot Program Design",
    step2Desc: "Select a high-impact use case and define success metrics.",
    step3Title: "Implementation Kickoff",
    step3Desc: "Begin our 90-day transformation program.",
    tier1Title: "Starter",
    tier1Price: "$25K-50K",
    tier1Desc: "Basic models, dashboard, 3-month implementation.",
    tier1Roi: "3-5x ROI in 6 months",
    tier2Title: "Pro",
    tier2Price: "$50K-100K",
    tier2Desc: "Advanced models, real-time personalization, full platform.",
    tier2Roi: "5-8x ROI in 6 months",
    tier3Title: "Enterprise",
    tier3Price: "$100K-250K",
    tier3Desc: "Custom models, multi-channel optimization, dedicated team.",
    tier3Roi: "8-12x ROI in 12 months",
    specialOfferTitle: "Special Offer",
    offerTitle: "Free Customer Analytics Audit",
    offerValue: "($5,000 Value)",
    offerPoint1: "Complete assessment of your customer data",
    offerPoint2: "Identification of top optimization opportunities",
    offerPoint3: "Custom demo using your data",
    ctaButton: "Claim Your Free Audit",
    questionsTitle: "Questions & Discussion",
    questionsSubtitle: "Ready to turn your customer data into competitive advantage?",
    contactEmail: "info@aidatasolutions.com",
    contactPhone: "+1 (555) 123-4567",
    contactSchedule: "Schedule a Demo",
  },
  zh: {
    // App Titles
    title: "AI驱动的客户分析",
    // Slide Titles
    introduction: "介绍",
    businessChallenges: "业务挑战",
    solution: "AI解决方案",
    technologyOverview: "平台技术",
    industryApplications: "行业应用",
    successStory: "成功案例",
    implementation: "实施计划",
    gettingStarted: "开始使用",
    // Navigation
    previous: "上一页",
    next: "下一页",
    slide: "幻灯片",
    of: "之",
    // IntroductionSlide (Slide 1 & 2)
    slide1Title: "AI驱动的客户分析",
    slide1Subtitle: "了解AI时代的客户分析",
    agendaTitle: "今日议程:",
    agenda1: "什么是客户分析及其重要性？",
    agenda2: "从传统分析到AI驱动分析的演变",
    agenda3: "客户分析解决的关键业务挑战",
    agenda4: "我们全面的客户分析解决方案",
    agenda5: "真实世界的应用和成功案例",
    agenda6: "如何开始使用客户分析",
    slide2Title: "什么是客户分析？",
    slide2Definition: "对客户数据进行系统性检查，以了解驱动业务决策和改善客户体验的行为模式、偏好和趋势。",
    slide2BottomLine: "客户分析将原始数据转化为战略性商业智能。",
    coreComponentsTitle: "核心组成部分",
    dataCollection: "数据收集",
    patternRecognition: "模式识别",
    segmentation: "客户分群",
    predictiveModeling: "预测建模",
    actionableInsights: "可行的见解",
    keyQuestionsTitle: "回答的关键问题",
    qValuableCustomers: "谁是我最有价值的客户？",
    qPurchaseDecisions: "是什么驱动客户的购买决策？",
    qBuyOrChurn: "客户最可能在什么时候购买或流失？",
    qRecommendations: "我应该向特定客户推荐哪些产品？",
    qOptimizeJourney: "如何优化客户旅程？",
    // BusinessChallengesSlide (Slide 3)
    slide3Title: "为什么客户分析在今天至关重要",
    slide3Subtitle: "商业的当务之急",
    marketRealitiesTitle: "驱动需求的市场现实",
    realityTitle1: "客户期望",
    reality1Point1: "80%的客户期望个性化体验",
    reality1Point2: "73%的客户愿意为个性化支付更多",
    reality1Point3: "91%的客户偏爱提供相关推荐的品牌",
    realityTitle2: "经济压力",
    reality2Point1: "客户获取成本同比增长60%",
    reality2Point2: "保留客户比获取新客户便宜5-25倍",
    reality2Point3: "使用分析的公司表现优于同行的可能性高23%",
    realityTitle3: "数据爆炸",
    reality3Point1: "每个客户每天产生2.5万亿字节的数据",
    reality3Point2: "全球90%的数据是过去两年内创建的",
    reality3Point3: "传统方法无法处理如此巨大的数据量",
    consequencesTitle: "无分析的业务后果",
    consequenceTitle1: "收入损失",
    consequenceText1: "电子商务中70%的购物车放弃率",
    consequenceTitle2: "营销浪费",
    consequenceText2: "50%的预算花在了错误的客户身上",
    consequenceTitle3: "错失机会",
    consequenceText3: "85%的企业无法预测客户流失",
    consequenceTitle4: "竞争劣势",
    consequenceText4: "使用AI的竞争对手获得15-20%的市场份额优势",
    slide3BottomLine: "在当今市场，客户分析不是可选项，而是生存的必需品。",
    // SolutionSlide (Slide 4 & 5)
    slide4Title: "传统分析 vs. AI驱动分析",
    slide4Subtitle: "客户理解的演变",
    capability: "能力",
    traditional: "传统",
    aiPowered: "AI驱动",
    dataProcessing: "数据处理",
    batchProcessing: "批处理，每周/每月报告",
    realTimeProcessing: "实时处理，即时洞察",
    analysisScope: "分析范围",
    historical: "历史数据，发生了什么",
    predictive: "预测性和规定性分析",
    personalization: "个性化",
    basicSegmentation: "基本分段（年龄、地点）",
    individualized: "个人级定制",
    patternDetection: "模式检测",
    manual: "人力驱动，变量有限",
    automated: "AI识别隐藏模式",
    actionability: "可操作性",
    manualInterpretation: "需要手动解读",
    automatedRecommendations: "自动化推荐和警报",
    slide5Title: "解决的关键业务问题",
    slide5Subtitle: "应对您最大的挑战",
    challenge: "挑战",
    aiSolution: "AI解决方案",
    result: "结果",
    problem1Title: "客户流失",
    problem1Challenge: "年均流失率25%；更换成本高出5-25倍。",
    problem1Solution: "预测性流失模型提前30-90天识别风险客户。",
    problem1Result: "客户流失率降低高达65%。",
    problem2Title: "无效的个性化",
    problem2Challenge: "通用体验的转化率低于2%。",
    problem2Solution: "实时个性化引擎提供个人级推荐。",
    problem2Result: "转化率提高高达34%。",
    problem3Title: "营销投资回报率低",
    problem3Challenge: "50%的营销支出用于低价值潜在客户。",
    problem3Solution: "通过预测性生命周期价值模型进行智能定位。",
    problem3Result: "营销投资回报率提高高达240%。",
    problem4Title: "购物车放弃",
    problem4Challenge: "平均70%的购物车放弃率，全球损失4.6万亿美元。",
    problem4Solution: "通过实时干预触发器进行智能恢复。",
    problem4Result: "购物车恢复率提高高达92%。",
    // TechnologyOverviewSlide (Slide 6)
    slide6Title: "我们的AI驱动客户分析解决方案",
    slide6Subtitle: "综合智能平台",
    architectureTitle: "平台架构",
    layer1Title: "数据集成",
    layer1Point1: "连接50多个数据源",
    layer1Point2: "实时处理（10M+事件/小时）",
    layer1Point3: "自动化数据质量",
    layer2Title: "AI分析引擎",
    layer2Point1: "高级机器学习算法",
    layer2Point2: "行业特定模型",
    layer2Point3: "持续学习",
    layer3Title: "智能仪表板",
    layer3Point1: "实时洞察和警报",
    layer3Point2: "预测性推荐",
    layer3Point3: "自定义报告",
    layer4Title: "行动层",
    layer4Point1: "自动化营销活动触发器",
    layer4Point2: "API集成",
    layer4Point3: "性能跟踪",
    capabilitiesTitle: "核心能力",
    capability1Title: "实时智能",
    capability1Point1: "实时活动监控",
    capability1Point2: "即时流失警报",
    capability1Point3: "动态客户分群",
    capability2Title: "预测分析",
    capability2Point1: "生命周期价值预测（87%准确率）",
    capability2Point2: "流失预测（94%准确率）",
    capability2Point3: "购买意图建模",
    capability2Point4: "季节性需求预测",
    capability3Title: "AI驱动的推荐",
    capability3Point1: "个性化产品建议",
    capability3Point2: "交叉销售和增销机会",
    capability3Point3: "最佳时间和渠道",
    capability3Point4: "内容个性化",
    capability4Title: "商业智能",
    capability4Point1: "客户旅程地图",
    capability4Point2: "收入归因分析",
    capability4Point3: "营销活动效果衡量",
    capability4Point4: "竞争基准",
    // IndustryApplicationsSlide (Slide 7)
    slide7Title: "行业特定应用",
    slide7Subtitle: "为您的业务量身定制的解决方案",
    useCases: "主要用例:",
    typicalResults: "典型结果:",
    industry1Title: "电子商务与零售",
    industry1Case1: "产品推荐引擎",
    industry1Case2: "动态定价优化",
    industry1Case3: "库存需求预测",
    industry1Case4: "客户旅程优化",
    industry1Result1: "平均订单价值增加23%",
    industry1Result2: "转化率提高45%",
    industry1Result3: "库存成本降低32%",
    industry2Title: "B2B与SaaS",
    industry2Case1: "账户扩展机会",
    industry2Case2: "客户健康评分",
    industry2Case3: "续约概率预测",
    industry2Case4: "销售管道优化",
    industry2Result1: "扩展收入增加28%",
    industry2Result2: "续约率提高67%",
    industry2Result3: "客户获取成本降低34%",
    industry3Title: "医疗保健与专业服务",
    industry3Case1: "患者/客户旅程优化",
    industry3Case2: "服务使用预测",
    industry3Case3: "满意度和保留模型",
    industry3Case4: "资源分配优化",
    industry3Result1: "满意度提高19%",
    industry3Result2: "服务使用率增加25%",
    industry3Result3: "未到诊率降低31%",
    industry4Title: "金融服务",
    industry4Case1: "客户生命周期价值建模",
    industry4Case2: "交叉销售优化",
    industry4Case3: "风险评估与欺诈检测",
    industry4Case4: "数字互动优化",
    industry4Result1: "交叉销售成功率增加42%",
    industry4Result2: "数字采用率提高58%",
    industry4Result3: "客户流失率降低29%",
    // SuccessStorySlide (Slide 8)
    slide8Title: "成功案例：家居电商",
    slide8Subtitle: "真实实施的真实成果",
    clientBackgroundTitle: "客户背景",
    clientBackgroundContent: "中型在线零售商（年收入5000万美元，200多万客户）面临购物车放弃和客户终身价值下降的困境。",
    challengesTitle: "挑战",
    challenge1: "70%的购物车放弃率",
    challenge2: "复购率下降",
    challenge3: "电子邮件营销效果不佳",
    challenge4: "无法预测季节性需求",
    solutionTitle: "实施的解决方案",
    phase1: "1-4周：整合数据源并实施实时行为跟踪。",
    phase2: "5-8周：部署预测性流失/LTV模型和个性化推荐引擎。",
    phase3: "9-12周：微调AI模型并将个性化扩展到电子邮件营销活动。",
    resultsTitle: "量化成果（90天）",
    metric: "指标",
    before: "之前",
    after: "之后",
    improvement: "提升",
    metric1: "购物车恢复率",
    metric2: "客户终身价值",
    metric3: "邮件点击率",
    metric4: "转化率",
    metric5: "平均订单价值",
    revenueImpactTitle: "收入影响",
    impact1Value: "24.8万美元",
    impact1Desc: "额外月收入",
    impact2Value: "297万美元",
    impact2Desc: "预计年影响",
    impact3Value: "534%",
    impact3Desc: "第一年投资回报率",
    keyInsightsTitle: "交付的关键AI洞察",
    insight1: "同时查看灯具和家具的客户LTV高出45%。",
    insight2: "周日晚上7点是邮件营销的最佳时间（参与度+34%）。",
    insight3: "比竞争对手提前2个月检测到极简主义风格趋势。",
    // ImplementationSlide (Slide 9)
    slide9Title: "实施方法与时间表",
    slide9Subtitle: "您的客户分析成功之路",
    frameworkTitle: "我们成熟的90天框架",
    phase1Title: "探索",
    phase1Duration: "1-3周",
    phase1Item1: "数据评估",
    phase1Item2: "业务对齐",
    phase1Item3: "基础设施设置",
    phase1Item4: "基线测量",
    phase2Title: "AI部署",
    phase2Duration: "4-8周",
    phase2Item1: "模型训练",
    phase2Item2: "仪表板实施",
    phase2Item3: "团队培训",
    phase2Item4: "初步优化",
    phase3Title: "优化",
    phase3Duration: "9-12周",
    phase3Item1: "性能调优",
    phase3Item2: "激活高级功能",
    phase3Item3: "成功衡量",
    phase3Item4: "路线图规划",
    riskMitigationTitle: "风险缓解",
    risk1: "分阶段方法：在每个阶段交付价值。",
    risk2: "成功指标：明确的KPI和衡量框架。",
    risk3: "持续支持：专属的客户成功团队。",
    risk4: "性能保证：投资回报率承诺。",
    teamRoleTitle: "您团队的角色",
    role1: "最少的IT资源（每周2-4小时）。",
    role2: "业务利益相关者输入（每周签到）。",
    role3: "提供变更管理支持。",
    role4: "持续优化合作伙伴关系。",
    // GettingStartedSlide (Slide 10)
    slide10Title: "开始使用",
    slide10Subtitle: "准备好改变您对客户的理解了吗？",
    nextStepsTitle: "后续步骤",
    investmentTitle: "投资",
    step1Title: "免费客户分析评估",
    step1Desc: "审计您当前的能力并获得定制的投资回报率预测。",
    step2Title: "试点项目设计",
    step2Desc: "选择一个高影响力的用例并定义成功指标。",
    step3Title: "实施启动",
    step3Desc: "开始我们为期90天的转型计划。",
    tier1Title: "入门版",
    tier1Price: "2.5万-5万美元",
    tier1Desc: "基础模型，仪表板，3个月实施。",
    tier1Roi: "6个月内3-5倍投资回报率",
    tier2Title: "专业版",
    tier2Price: "5万-10万美元",
    tier2Desc: "高级模型，实时个性化，完整平台。",
    tier2Roi: "6个月内5-8倍投资回报率",
    tier3Title: "企业版",
    tier3Price: "10万-25万美元",
    tier3Desc: "定制模型，多渠道优化，专属团队。",
    tier3Roi: "12个月内8-12倍投资回报率",
    specialOfferTitle: "特别优惠",
    offerTitle: "免费客户分析审计",
    offerValue: "（价值5000美元）",
    offerPoint1: "全面评估您的客户数据",
    offerPoint2: "识别最佳优化机会",
    offerPoint3: "使用您的数据进行定制演示",
    ctaButton: "申请免费审计",
    questionsTitle: "问题与讨论",
    questionsSubtitle: "准备好将您的客户数据转化为竞争优势了吗？",
    contactEmail: "info@aidatasolutions.com",
    contactPhone: "+1 (555) 123-4567",
    contactSchedule: "安排演示",
  },
}; 